import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle2, Clock, XCircle, Home, Loader2 } from "lucide-react";

export default function CheckoutResult({ status }) {
  const [params] = useSearchParams();
  const orderId = params.get("order");
  const [order, setOrder] = useState(null);
  const [confirming, setConfirming] = useState(status === "success" || status === "pending");

  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;
    const run = async () => {
      // 1) Notify backend immediately — locks order (blocks edit/cancel) and triggers a sync MP reconcile
      if (status === "success" || status === "pending") {
        try {
          const r = await api.post(`/orders/${orderId}/payment-returned`);
          if (cancelled) return;
          // 2) If still not paid, keep polling for a few seconds (cron also runs in background)
          if (r.data?.status !== "paid") {
            for (let i = 0; i < 6 && !cancelled; i++) {
              await new Promise((res) => setTimeout(res, 2500));
              try {
                const list = await api.get("/orders/mine");
                const fresh = list.data.find((o) => o.id === orderId);
                if (fresh) setOrder(fresh);
                if (fresh?.status === "paid") break;
              } catch (_) { /* ignore */ }
            }
          }
        } catch (_) { /* ignore */ }
      }
      try {
        const list = await api.get("/orders/mine");
        if (!cancelled) setOrder(list.data.find((o) => o.id === orderId) || null);
      } catch (_) { /* ignore */ }
      if (!cancelled) setConfirming(false);
    };
    run();
    return () => { cancelled = true; };
  }, [orderId, status]);

  const resolved = (() => {
    if (status !== "success") return status;
    if (!order) return "pending"; // ainda confirmando
    return order.status === "paid" ? "success" : "pending";
  })();

  const conf = {
    success: { icon: CheckCircle2, color: "text-emerald-600 bg-emerald-50 border-emerald-200", title: "Pagamento confirmado!", body: "Seu pedido foi pago com sucesso e já está visível para o comerciante." },
    pending: { icon: Clock, color: "text-amber-600 bg-amber-50 border-amber-200", title: "Confirmando pagamento...", body: "Recebemos o retorno do Mercado Pago. Estamos validando o pagamento — em até 1 minuto seu pedido será atualizado." },
    failure: { icon: XCircle, color: "text-red-600 bg-red-50 border-red-200", title: "Pagamento não concluído", body: "Tente novamente ou escolha outra forma de pagamento." },
  }[resolved] || {};

  const Icon = confirming ? Loader2 : (conf.icon || CheckCircle2);
  return (
    <div className="max-w-2xl mx-auto px-4 py-12 text-center">
      <Card className={`p-8 rounded-3xl border-2 ${conf.color}`}>
        <Icon size={56} className={`mx-auto mb-3 ${confirming ? "animate-spin" : ""}`}/>
        <h1 className="font-display text-3xl font-bold" data-testid="checkout-result-title">{confirming ? "Confirmando pagamento..." : conf.title}</h1>
        <p className="text-stone-600 mt-2">{confirming ? "Recebemos o retorno do Mercado Pago. Estamos validando." : conf.body}</p>
        {order && (
          <div className="mt-5 text-left text-sm bg-white p-4 rounded-xl border border-stone-200" data-testid="checkout-result-order">
            <p><strong>Pedido #{order.id.slice(0, 8)}</strong> · <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
              order.status === "paid" ? "bg-emerald-100 text-emerald-800" :
              order.status === "cancelled" ? "bg-red-100 text-red-800" :
              order.awaiting_confirmation ? "bg-amber-100 text-amber-800" :
              "bg-stone-100 text-stone-700"
            }`}>{
              order.status === "paid" ? "Pago" :
              order.status === "cancelled" ? "Cancelado" :
              order.awaiting_confirmation ? "Confirmando pagamento" :
              "Aguardando pagamento"
            }</span></p>
            <p>Empresa: {order.company_name}</p>
            <p>Total: R$ {order.subtotal.toFixed(2)} (BemCash R$ {order.cashback_used.toFixed(2)} + MP R$ {order.mp_amount.toFixed(2)})</p>
            {order.expected_cashback_earn > 0 && order.status !== "paid" && <p className="text-emerald-700">Você ganhará R$ {order.expected_cashback_earn.toFixed(2)} de BemCash quando confirmado</p>}
            {order.status === "paid" && order.expected_cashback_earn > 0 && <p className="text-emerald-700 font-semibold">+R$ {order.expected_cashback_earn.toFixed(2)} de BemCash creditados na sua carteira</p>}
          </div>
        )}
        <Link to="/wallet"><Button className="mt-6 bg-blue-900 text-white rounded-xl" data-testid="checkout-result-wallet"><Home size={14} className="mr-2"/> Ir para minha Carteira</Button></Link>
      </Card>
    </div>
  );
}
